import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Users, Coins } from "lucide-react";
import { sportInfo, formatMatchTime, formatRupiah } from "../lib/api";

export default function MatchCard({ match, compact = false }) {
  const sport = sportInfo(match.sport);
  const playerCount = match.player_ids?.length || 0;
  const remaining = match.max_players - playerCount;
  const isFull = match.status === "FULL" || remaining <= 0;
  const fillPct = Math.min(100, (playerCount / match.max_players) * 100);

  return (
    <Link
      to={`/match/${match.match_id}`}
      data-testid={`match-card-${match.match_id}`}
      className={`block bg-card border border-border rounded-2xl ${compact ? "p-4 min-w-[280px]" : "p-5"} hover:-translate-y-0.5 hover:border-primary/40 transition-all duration-300 relative overflow-hidden group`}
    >
      <div
        aria-hidden
        className="absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl opacity-20"
        style={{ background: sport.color }}
      />

      <div className="flex items-start justify-between gap-3 relative">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-11 w-11 rounded-2xl flex items-center justify-center text-2xl shrink-0"
            style={{ background: sport.color + "22", border: `1px solid ${sport.color}44` }}
          >
            <span>{sport.icon}</span>
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-base leading-tight truncate">{sport.label}</div>
            <div className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3 shrink-0" /> {match.venue}
            </div>
          </div>
        </div>
        <SlotBadge remaining={remaining} max={match.max_players} isFull={isFull} />
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {formatMatchTime(match.starts_at)}</div>
        <div className="flex items-center gap-1.5"><Coins className="h-3.5 w-3.5" /> {formatRupiah(match.cost_per_player)}</div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full slot-fill-anim"
            style={{ width: `${fillPct}%`, background: isFull ? "#10B981" : "#00E5FF" }}
          />
        </div>
        <div className="flex items-center gap-1 text-xs font-bold text-primary">
          <Users className="h-3.5 w-3.5" /> {playerCount}/{match.max_players}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-1 rounded-full">
          {match.skill_level === "all" ? "Semua Level" : match.skill_level}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-1 rounded-full">
          {match.city}
        </span>
        {match.team && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 border border-primary/30 px-2 py-1 rounded-full inline-flex items-center gap-1">
            {match.team.logo ? <img src={match.team.logo} alt="" className="h-3 w-3 rounded-full object-cover" /> : "🏆"}
            {match.team.name}
          </span>
        )}
        {match.status === "FULL" && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full">FULL</span>
        )}
      </div>
    </Link>
  );
}

function SlotBadge({ remaining, max, isFull }) {
  if (isFull) {
    return (
      <div className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-400/10 px-2.5 py-1.5 rounded-full border border-emerald-400/30">
        FULL
      </div>
    );
  }
  const urgent = remaining === 1;
  return (
    <div
      className={`shrink-0 flex items-center gap-1.5 text-xs font-bold px-2.5 py-1.5 rounded-full border ${
        urgent
          ? "text-primary bg-primary/10 border-primary/40 pulse-cyan"
          : "text-primary bg-primary/10 border-primary/20"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
      {remaining} slot
    </div>
  );
}
